import { IconButton } from "@mui/material"
import { VisibilityOutlined } from "@mui-symbols-material/w300"
import { useNavigate } from "@tanstack/react-router"
import type { CellContext, ColumnDef } from "@tanstack/react-table"
import { useCallback, useMemo } from "react"
import type { ReactElement } from "react"

import { findCompanyType } from "../../utils/company"

import Chip from "@/components/common/Chip/Chip"
import LimitedSizeChipGroup from "@/components/common/Chip/LimitedSizeChipGroup"
import DisplayAS400Id from "@/components/common/DisplayAS400Id/DisplayAS400Id"
import { StatusIndicator } from "@/components/common/StatusIndicator/StatusIndicator"
import type { Company, CustomerAccountStatus, User } from "@/graphql/codegen/graphql"
import { CompanySortField, CompanyStatus } from "@/graphql/codegen/graphql"
import { ManagersCell } from "@/screens/Companies/components/tables/ManagersCell"
import { CUSTOMER_ACCOUNT_STATUS_LABEL_MAP } from "@/screens/Companies/constants/customerAccountDataMap"
import { useCompanyTypesStore } from "@/screens/Companies/stores/useCompanyTypesStore"
import { createStatusMap } from "@/utils/statusMap"

export const companyStatusMap = createStatusMap<
  CompanyStatus,
  "primary" | "secondary" | "default" | "error" | "info" | "success" | "warning"
>([
  [CompanyStatus.Active, "success"],
  [CompanyStatus.Pending, "warning"],
  [CompanyStatus.Rejected, "error"],
  [CompanyStatus.Inactive, "secondary"],
])

// AK-6592: The Managers column lists the account managers and the product managers of a company.
export const getCompanyManagers = (company: Company): User[] => [
  ...company.accountManagers,
  ...(company.productManagers ?? []),
]

export type SortableAccessorKey = "accountManagers" | "types" | "name"

export const accessorKeyToCompanySortField: ReadonlyMap<SortableAccessorKey, CompanySortField> = new Map([
  ["accountManagers", CompanySortField.AccountManager],
  ["types", CompanySortField.CompanyType],
  ["name", CompanySortField.Name],
])

export const companySortFieldToAccessoryKey: ReadonlyMap<CompanySortField, SortableAccessorKey> = new Map([
  [CompanySortField.AccountManager, "accountManagers"],
  [CompanySortField.CompanyType, "types"],
  [CompanySortField.Name, "name"],
])

const CompanyTypeCell = ({ types, maxSize }: { types: string[]; maxSize: number }): ReactElement => {
  const companyTypes = useCompanyTypesStore((state) => state.companyTypes)
  const getChipName = useCallback((type: string) => findCompanyType(companyTypes, type)?.name, [companyTypes])

  const chips = useMemo(() => types.map((type, index) => ({ id: `${type}-${index}`, name: type })), [types])

  return (
    <LimitedSizeChipGroup
      chips={chips}
      maxSize={maxSize}
      renderChip={(chip) => {
        const label = getChipName(chip.name)
        return (
          <Chip
            className='mr-2 rounded'
            key={chip.id}
            label={label}
            icon={chip?.icon}
            color='primary'
            style={{ maxWidth: chip?.maxWidth ?? "auto" }}
          />
        )
      }}
    />
  )
}

export const useCompanyColumns = (): ColumnDef<Company>[] => {
  const navigate = useNavigate()
  const handleCompanyRowClick = (companyId: string) =>
    void navigate({ from: "/app/companies", to: `${companyId}/edit` })

  return useMemo(
    () => [
      {
        accessorKey: "status",
        header: "",
        cell: ({ getValue, cell }: CellContext<Company, unknown>): ReactElement => (
          <StatusIndicator
            data-testid={`${cell.id}-${getValue()}`}
            status={getValue() as string}
            map={companyStatusMap}
          />
        ),
        size: 24,
        enableSorting: false,
      },
      {
        accessorKey: "name",
        header: "Company Name",
        sortingFn: "alphanumeric",
        enableSorting: true,
        size: 250,
      },
      {
        accessorKey: "publicId",
        header: "Company ID",
        cell: ({ row }: CellContext<Company, unknown>): ReactElement => (
          <div className='flex flex-col items-start gap-1'>
            <span className='text-gray-700'>{row.original.publicId}</span>
            <DisplayAS400Id id={row.original.externalSystemId ?? row.original.customerExternalSystemId} />
          </div>
        ),
        enableSorting: false,
        size: 136,
      },
      {
        accessorKey: "description",
        header: () => <span className='whitespace-nowrap'>Company Description</span>,
        enableSorting: false,
      },
      {
        accessorKey: "types",
        header: "Company Type",
        cell: ({ getValue, cell }: CellContext<Company, unknown>): ReactElement => (
          <CompanyTypeCell types={getValue() as string[]} maxSize={cell.column.getSize()} />
        ),
        enableSorting: true,
        size: 200,
      },
      {
        accessorKey: "customerAccountStatus",
        header: () => <span className='whitespace-nowrap'>Customer Account Status</span>,
        cell: ({ row }) => {
          const customerAccountStatus = row.original.customerAccountStatus

          return customerAccountStatus
            ? CUSTOMER_ACCOUNT_STATUS_LABEL_MAP[customerAccountStatus as CustomerAccountStatus]
            : ""
        },

        enableSorting: false,
        size: 224,
      },
      {
        id: "accountManagers",
        header: "Managers",
        accessorFn: getCompanyManagers,
        cell: ({ row }: CellContext<Company, unknown>): ReactElement => (
          <ManagersCell managers={getCompanyManagers(row.original)} />
        ),
        sortingFn: "alphanumeric",
        enableSorting: true,
        size: 200,
      },
      {
        accessorKey: "actions",
        header: "",
        cell: ({ row }) => (
          <IconButton
            onClick={() => handleCompanyRowClick(row.original.companyId)}
            onKeyUp={(e) => e.key === "Enter" && handleCompanyRowClick(row.original.companyId)}
          >
            <VisibilityOutlined className='block text-gray-600' />
          </IconButton>
        ),
        size: 52,
        enableSorting: false,
      },
    ],
    // eslint-disable-next-line react-hooks/exhaustive-deps
    []
  )
}
